import React from 'react'

import { Box, Card, CardImage } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'
import Head from 'next/head'

import { STYLES } from '@/constants'
import { buildExternalImageUrl } from '@/utils/build-external-image-url'

import styles from './styles.module.scss'

export const EXTERNAL_IMAGE_URL_MID_PATHS = {
    CATEGORY: '/t_category_hero_image',
    SUBCATEGORY: '/t_category_hero_image',
    PERFORMER: '/t_performer_hero_image',
    VENUE: '/t_venue_hero_image',
}

export interface FadeOptions {
    mobile: boolean
    desktop: boolean
}

interface HeaderCardImageProps {
    mobileImageUrl?: string
    desktopImageUrl?: string
    externalImageMidPathKey: keyof typeof EXTERNAL_IMAGE_URL_MID_PATHS
    externalImageFilePath?: string
    additionalClassNames?: { [key: string]: boolean }
    fadeOptions?: FadeOptions
    externalImageAlt?: string
}

const HeaderCardImage: React.FC<HeaderCardImageProps> = ({
    mobileImageUrl,
    desktopImageUrl,
    externalImageMidPathKey,
    externalImageFilePath,
    additionalClassNames,
    fadeOptions = { mobile: false, desktop: false },
    children,
    externalImageAlt,
}) => {
    const externalImagePrefix = `${process.env.NEXT_PUBLIC_EXTERNAL_IMAGE_URL_PREFIX}`
    const externalImageDimensions = {
        width: 840,
        height: 360,
    }
    const externalImageUrl = buildExternalImageUrl(
        externalImagePrefix,
        externalImageFilePath,
        externalImageDimensions.width,
        externalImageDimensions.height,
    )

    const externalImageWrapper = (element: JSX.Element) => (
        <picture data-testid="picture-wrapper">
            <source
                srcSet={buildExternalImageUrl(externalImagePrefix, externalImageFilePath, 450, 137)}
                media="(max-width: 450px)"
            />
            <source
                srcSet={buildExternalImageUrl(externalImagePrefix, externalImageFilePath, 735, 224)}
                media="(max-width: 767px)"
            />
            <source
                srcSet={buildExternalImageUrl(externalImagePrefix, externalImageFilePath, 990, 288)}
                media="(max-width: 1023px)"
            />
            {element}
        </picture>
    )

    const renderCardImage = (options: { fade: boolean }) => (
        <CardImage
            fade={options.fade}
            lazyLoadingEnabled={false}
            mobileImage={mobileImageUrl}
            desktopImage={desktopImageUrl}
            externalImage={externalImageUrl}
            imgContainerClassName={styles.imgContainer}
            externalImageAlt={externalImageAlt}
            externalImageWidth={externalImageDimensions.width}
            externalImageHeight={externalImageDimensions.height}
            imageWrapperComponent={(element) => externalImageWrapper(element)}
        />
    )

    const { mobile: mobileFade, desktop: desktopFade } = fadeOptions
    const mobileCardImage = renderCardImage({ fade: mobileFade })
    const desktopCardImage = renderCardImage({ fade: desktopFade })
    const externalCardImage = renderCardImage({ fade: false })

    return (
        <>
            {(mobileImageUrl || desktopImageUrl || externalImageUrl) && (
                <Head>
                    {externalImageUrl ? (
                        <link
                            key="important-hero-image-external"
                            rel="preload"
                            href={externalImageUrl}
                            as="image/*"
                            data-testid="external-image-preload-link"
                        />
                    ) : (
                        <>
                            <link
                                key="important-hero-image-mobile"
                                rel="preload"
                                href={mobileImageUrl || desktopImageUrl}
                                as="image/*"
                                data-testid="mobile-image-preload-link"
                                media={`(max-width: ${STYLES.breakpoint.tablet - 1}px)`}
                            />
                            <link
                                key="important-hero-image-desktop"
                                rel="preload"
                                href={desktopImageUrl || mobileImageUrl}
                                as="image/*"
                                data-testid="desktop-image-preload-link"
                                media={`(min-width: ${STYLES.breakpoint.tablet}px)`}
                            />
                        </>
                    )}
                </Head>
            )}
            <Card
                className={classNames(styles.card, additionalClassNames)}
                data-testid={`${externalImageMidPathKey.toLowerCase()}-hero`}
                roundedSize="base"
            >
                {externalImageUrl ? (
                    <Box>{externalCardImage}</Box>
                ) : (
                    <>
                        <Box className={styles.onlyShowOnMobileSmall} data-testid="mobile-container">
                            {mobileCardImage}
                        </Box>
                        <Box className={styles.onlyShowOnWiderThanMobileSmall} data-testid="desktop-container">
                            {desktopCardImage}
                        </Box>
                    </>
                )}
                {children}
            </Card>
        </>
    )
}

export default HeaderCardImage
